import React, { useEffect, useMemo, useState } from 'react';
import { ReadingSettings } from './ReadingSettings';
import { useReadingPrefs } from './readingPrefs';
import { useTranslation } from 'react-i18next';
import { AlignLeft, ArrowRight, Clock, Eye, EyeOff, Loader2, Maximize2, Minimize2, Sigma } from 'lucide-react';
import { Button } from 'components/ui/button';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import { cn } from 'lib/utils';
import { useExamSession } from '../../hooks/useExamSession';
import { useFullscreen } from '../../hooks/useFullscreen';
import { formatClock, isAnswered, isSatKind, KIND_LABEL_KEY } from '../../constants';
import type { ExamConfig } from '../../types';
import { IeltsSectionView } from './IeltsSectionView';
import { SatModuleView } from './SatModuleView';
import { ReferenceSheet } from './ReferenceSheet';
import { clearHighlights } from './highlightStore';
import { clearSatModuleState } from './satModuleState';
import { clearSectionPart } from './sectionPart';

interface Props {
  attemptId: string;
  testTitle: string;
  examConfig: ExamConfig;
  initialAnswers: Map<string, unknown>;
  /** Called once when the last section closes (by the candidate or the clock). */
  onFinished: (status: string) => void;
}

/**
 * Full-screen runner for IELTS-style and Digital SAT practice attempts.
 * Sections are timed and locked by the server; the runner shows the open one.
 */
export function StandardizedExamRunner({ attemptId, testTitle, examConfig, initialAnswers, onFinished }: Props) {
  const { t } = useTranslation();
  const reading = useReadingPrefs();
  const session = useExamSession({
    attemptId,
    initialAnswers,
    onFinished: (status) => {
      clearHighlights(attemptId);
      clearSatModuleState(attemptId);
      clearSectionPart(attemptId);
      onFinished(status);
    },
  });
  const { examState, section, remaining, onBreak } = session;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [timerHidden, setTimerHidden] = useState(false);
  const [referenceOpen, setReferenceOpen] = useState(false);
  const sat = examConfig.standard === 'SAT';
  const tools = examConfig.sat?.tools;
  // Full screen is offered on IELTS Reading and on every Digital SAT module. It
  // is left when the candidate moves to a section without it, or the runner closes.
  const fullscreenAllowed = sat || section?.kind === 'IELTS_READING';
  const fullscreen = useFullscreen();
  const { exit: exitFullscreen } = fullscreen;
  useEffect(() => (fullscreenAllowed ? exitFullscreen : undefined), [fullscreenAllowed, exitFullscreen]);

  const position = useMemo(() => {
    if (!examState) return { index: 0, total: 0 };
    const index = examState.sections.findIndex((s) => s.id === examState.activeSectionId);
    return { index, total: examState.sections.length };
  }, [examState]);
  const isLast = position.total > 0 && position.index === position.total - 1;
  // Name the section the button leads to: a bare "Next section" left candidates
  // unsure whether it meant the next part of this section or a new section.
  const sectionName = (index: number) => {
    const meta = examState?.sections[index];
    return meta ? meta.title?.trim() || t(KIND_LABEL_KEY[meta.kind]) : '';
  };
  const nextName = isLast ? '' : sectionName(position.index + 1);
  const advanceLabel = isLast ? t('exam.finish') : nextName ? t('exam.go_to_section', { name: nextName }) : t('exam.next_section');
  const confirmTitle = isLast
    ? t('exam.confirm.finish_title')
    : nextName
      ? t('exam.confirm.go_to_title', { current: sectionName(position.index), next: nextName })
      : t('exam.confirm.next_title');
  const unanswered = section ? section.questions.filter((q) => !isAnswered(session.answers.get(q.id))).length : 0;
  const lowTime = remaining !== null && remaining <= 300;

  if (session.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white" role="status">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" aria-hidden />
        <span className="sr-only">{t('loading')}</span>
      </div>
    );
  }

  if (!examState) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white p-6 text-center">
        <p className="text-gray-700">{session.error || t('exam.load_error')}</p>
        <Button onClick={session.refresh}>{t('exam.retry')}</Button>
      </div>
    );
  }

  if (onBreak) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A1628] px-6 text-center text-white">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{t('exam.break.eyebrow')}</p>
        <h1 className="mt-3 text-3xl font-bold sm:text-4xl">{t('exam.break.title')}</h1>
        <p className="mt-3 max-w-md text-slate-300">{t('exam.break.body')}</p>
        <p className="mt-10 font-mono text-6xl font-semibold tabular-nums sm:text-7xl" aria-live="off">
          {formatClock(remaining ?? 0)}
        </p>
        <Button size="lg" onClick={session.resumeBreak} disabled={session.busy} className="mt-10 bg-primary-400 text-gray-950 hover:bg-primary-300">
          {t('exam.break.resume')}
        </Button>
        <p className="mt-4 text-xs text-slate-400">{t('exam.break.auto')}</p>
      </div>
    );
  }

  const activeMeta = examState.sections[position.index];

  const readingSettings = (className: string) => (
    <ReadingSettings
      prefs={reading.prefs}
      onTheme={(theme) => reading.update({ theme })}
      onZoomIn={reading.zoomIn}
      onZoomOut={reading.zoomOut}
      className={className}
    />
  );

  const fullscreenButton = (className: string) =>
    fullscreenAllowed && fullscreen.supported ? (
      <Button
        variant="ghost"
        size="icon"
        onClick={fullscreen.toggle}
        aria-label={fullscreen.active ? t('exam.exit_fullscreen') : t('exam.fullscreen')}
        title={fullscreen.active ? t('exam.exit_fullscreen') : t('exam.fullscreen')}
        className={cn('h-9 w-9 shrink-0', className)}
      >
        {fullscreen.active ? <Minimize2 className="h-4 w-4" aria-hidden /> : <Maximize2 className="h-4 w-4" aria-hidden />}
      </Button>
    ) : null;

  return (
    <div className="flex h-[100dvh] flex-col bg-white">
      {sat ? (
        <header
          className={cn(
            'flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-2.5 sm:px-6',
            sat ? 'border-b-2 border-dashed border-gray-300 bg-[#F5F7FB]' : 'border-gray-200 bg-[#0A1628] text-white',
          )}
        >
          <div className="min-w-0 flex-1 basis-full sm:basis-0">
            <p className={cn('truncate text-xs', sat ? 'text-gray-600' : 'text-slate-400')}>{testTitle}</p>
            <h1 className="truncate text-base font-semibold">
              {activeMeta?.title}
              <span className={cn('ml-2 text-sm font-normal', sat ? 'text-gray-500' : 'text-slate-400')}>
                {t('exam.section_of', { n: position.index + 1, total: position.total })}
              </span>
            </h1>
          </div>

          {remaining !== null && (
            <div className="flex items-center gap-2">
              {!timerHidden || lowTime ? (
                <span
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2.5 py-1 font-mono text-lg font-semibold tabular-nums',
                    lowTime ? 'bg-red-600 text-white' : sat ? 'text-gray-900' : 'text-white',
                  )}
                  role="timer"
                  aria-label={t('exam.time_left', { time: formatClock(remaining) })}
                >
                  <Clock className="h-4 w-4" aria-hidden /> {formatClock(remaining)}
                </span>
              ) : (
                <span className="text-sm text-gray-600">{t('exam.timer_hidden')}</span>
              )}
              {sat && (
                <Button variant="ghost" size="sm" onClick={() => setTimerHidden((v) => !v)} aria-pressed={timerHidden} className="gap-1 text-gray-700">
                  {timerHidden ? <Eye className="h-4 w-4" aria-hidden /> : <EyeOff className="h-4 w-4" aria-hidden />}
                  {timerHidden ? t('exam.show_timer') : t('exam.hide_timer')}
                </Button>
              )}
            </div>
          )}

          <div className="flex items-center gap-1.5">
            {readingSettings('text-gray-700 hover:bg-gray-200/70 hover:text-gray-900')}
            {fullscreenButton('text-gray-700 hover:bg-gray-200/70 hover:text-gray-900')}
            {sat && section?.kind === 'SAT_MATH' && tools?.referenceSheet !== false && (
              <Button variant="ghost" size="sm" onClick={() => setReferenceOpen(true)} className="gap-1.5">
                <Sigma className="h-4 w-4" aria-hidden /> {t('exam.sat.reference')}
              </Button>
            )}
            {!sat && (
              <Button
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={session.busy}
                className="bg-primary-400 text-gray-950 hover:bg-primary-300"
              >
                {advanceLabel}
              </Button>
            )}
          </div>
          {examState.versionCode && (
            <span className={cn('w-full text-right font-mono text-[11px] sm:w-auto', sat ? 'text-gray-500' : 'text-slate-400')}>
              {t('exam.version_code', { code: examState.versionCode })}
            </span>
          )}
        </header>
      ) : (
        <header className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-200 bg-white px-4 py-2.5 sm:h-[60px] sm:flex-nowrap sm:px-7 sm:py-0">
          <div className="flex min-w-0 flex-1 basis-full items-center gap-3 sm:basis-0 sm:gap-4">
            <span className="hidden h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-[#0A1628] sm:flex" aria-hidden>
              <AlignLeft className="h-4 w-4 text-primary" strokeWidth={2.4} />
            </span>
            <div className="min-w-0">
              <h1 className="truncate font-display text-sm font-bold text-gray-900 sm:text-[15px]">
                <span className="sm:hidden">{activeMeta?.title}</span>
                <span className="hidden sm:inline">{testTitle}</span>
              </h1>
              <p className="truncate text-xs text-gray-500">
                <span className="hidden sm:inline">{activeMeta?.title} · </span>
                {t('exam.section_of', { n: position.index + 1, total: position.total })}
              </p>
            </div>
          </div>

          <span
            className={cn('hidden items-center gap-1.5 text-xs md:inline-flex', session.saving ? 'text-gray-500' : 'text-green-700')}
            aria-hidden
          >
            <span className={cn('h-[7px] w-[7px] rounded-full', session.saving ? 'bg-gray-400' : 'bg-green-600')} />
            {session.saving ? t('exam.saving') : t('saved')}
          </span>

          {readingSettings('text-slate-500 hover:bg-slate-100 hover:text-slate-900')}
          {fullscreenButton('text-slate-500 hover:bg-slate-100 hover:text-slate-900')}

          {remaining !== null && (
            <span
              className={cn(
                'flex h-9 items-center gap-2 rounded-lg px-3 font-mono text-[15px] font-semibold tabular-nums',
                lowTime ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-900',
              )}
              role="timer"
              aria-label={t('exam.time_left', { time: formatClock(remaining) })}
            >
              <Clock className={cn('h-4 w-4', lowTime ? 'text-white' : 'text-slate-500')} aria-hidden /> {formatClock(remaining)}
            </span>
          )}

          <Button
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={session.busy}
            title={advanceLabel}
            className="h-9 min-w-0 gap-1.5 bg-[#0A1628] text-white hover:bg-[#16243d] max-sm:flex-1 sm:max-w-[16rem]"
          >
            <span className="min-w-0 truncate">{advanceLabel}</span>
            {!isLast && <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />}
          </Button>
          {examState.versionCode && (
            <span className="hidden font-mono text-[11px] text-gray-400 lg:inline">
              {t('exam.version_code', { code: examState.versionCode })}
            </span>
          )}
        </header>
      )}

      {session.saving && <span className="sr-only" role="status">{t('exam.saving')}</span>}

      {section ? (
        <div
          className="exam-reading flex min-h-0 flex-1 flex-col"
          data-reading-theme={reading.prefs.theme}
          // `zoom` scales the passage and questions together, layout included.
          style={{ zoom: reading.prefs.zoom } as React.CSSProperties}
        >
        {isSatKind(section.kind) ? (
          <SatModuleView
            key={section.id}
            attemptId={attemptId}
            section={section}
            answers={session.answers}
            onAnswer={session.setAnswer}
            annotatorEnabled={tools?.annotator !== false}
            eliminatorEnabled={tools?.eliminator !== false}
            onFinishModule={() => setConfirmOpen(true)}
            busy={session.busy}
          />
        ) : (
          <IeltsSectionView
            key={section.id}
            attemptId={attemptId}
            section={section}
            answers={session.answers}
            onAnswer={session.setAnswer}
            onRecorded={session.setLocalAnswer}
          />
        )}
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center p-6 text-gray-600">{t('exam.load_error')}</div>
      )}

      <ReferenceSheet open={referenceOpen} onClose={() => setReferenceOpen(false)} />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={confirmTitle}
        description={
          <>
            {t('exam.confirm.no_return')}
            {unanswered > 0 && <span className="mt-2 block font-medium text-amber-700">{t('exam.confirm.unanswered', { count: unanswered })}</span>}
          </>
        }
        confirmLabel={advanceLabel}
        cancelLabel={t('exam.confirm.stay')}
        loading={session.busy}
        onConfirm={async () => {
          await session.advance();
          setConfirmOpen(false);
        }}
      />
    </div>
  );
}

export default StandardizedExamRunner;
